/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { BoardType } from '@atlassian/jira-boards-common/src/model/index.tsx';
import { KANBAN, SCRUM } from '@atlassian/jira-common-directory-v2/src/model/constants.tsx';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { PROJECT_TITLE } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import {
	BoardActions,
	type BoardWithDataId,
} from '@atlassian/jira-navigation-board-action-menu/src/ui/BoardActions.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import BaseHeader from '@atlassian/jira-horizontal-nav-header/src/common/ui/BaseHeader.tsx';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import isWithinLast7Days from '@atlassian/jira-onboarding/src/utils/isWithinLast7Days.tsx';
import { ExtraComponentsFar } from '../extra-components-far/ExtraComponentsFar.tsx';
import { HorizontalNavProjectBreadcrumbs } from '../breadcrumbs/HorizontalNavProjectBreadcrumbs.tsx';
import { NavigationTeamButton } from '../team-button/NavigationTeamButton.tsx';
import { NavigationInviteButton } from '../invite-button/NavigationInviteButton.tsx';
import type { HorizontalNavBoardHeader_project$key } from './__generated__/HorizontalNavBoardHeader_project.graphql';
import type {
	JiraBoardType,
	HorizontalNavBoardHeader_board$key,
} from './__generated__/HorizontalNavBoardHeader_board.graphql';

type BoardProps = {
	boardFragment: HorizontalNavBoardHeader_board$key;
	projectFragment?: HorizontalNavBoardHeader_project$key | null;
};

export const HorizontalNavBoardHeader = ({
	boardFragment,
	projectFragment = null,
}: BoardProps): React.JSX.Element | null => {
	const [isFullscreen] = useIsFullscreen();
	const board = useFragment(
		graphql`
			fragment HorizontalNavBoardHeader_board on JiraBoard {
				__typename
				__id @required(action: THROW)
				boardId @required(action: THROW)
				name @required(action: THROW)
				canEdit
				boardType @required(action: THROW)
				favouriteValue {
					isFavourite @required(action: THROW)
				}
			}
		`,
		boardFragment,
	);

	const project = useFragment(
		graphql`
			fragment HorizontalNavBoardHeader_project on JiraProject
			@argumentDefinitions(
				showInviteButton: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-invite-button.relayprovider"
				}
			) {
				...NavigationTeamButton_project
				...NavigationInviteButton_project @include(if: $showInviteButton)
				canEditProjectConfig: action(type: EDIT_PROJECT_CONFIG) @include(if: $showInviteButton) {
					canPerform
				}
				projectId @include(if: $showInviteButton)
				created @include(if: $showInviteButton)
				...BoardActions @skip(if: $isTMPBoardRoute)
				...ExtraComponentsFar
				...HorizontalNavProjectBreadcrumbs
			}
		`,
		projectFragment,
	);

	const toBoardType = (jiraBoardType: JiraBoardType) => {
		if (jiraBoardType === 'SCRUM') {
			return SCRUM;
		}

		if (jiraBoardType === 'KANBAN') {
			return KANBAN;
		}
	};

	const boardData: BoardWithDataId | null = useMemo(() => {
		if (board?.__typename !== 'JiraBoard') {
			return null;
		}
		const isPublicBoard = !!board.name;
		return isPublicBoard
			? {
					id: board?.boardId?.toString(),
					dataId: board?.__id,
					admins: {
						users: [],
						groups: [],
					},
					name: board.name,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					type: toBoardType(board.boardType) as BoardType,
					favourite: board.favouriteValue?.isFavourite ?? false,
					canEdit: !!board.canEdit,
				}
			: {
					id: board?.boardId?.toString(),
					dataId: board?.__id,
					admins: {
						users: [],
						groups: [],
					},
					canEdit: !!board.canEdit,
					isPrivate: true,
				};
	}, [board]);

	const canEditProjectConfig = project?.canEditProjectConfig?.canPerform ?? false;

	const now = new Date();

	const showInviteButton =
		project?.created &&
		canEditProjectConfig &&
		project.projectId === '10000' && // only setting invite button for the default project
		isWithinLast7Days(project.created, now) &&
		expVal('jfind-restore-invite-button-experiment-2', 'enrolled', false);

	const analyticsData = useMemo(
		() => ({
			navigationContainer: PROJECT_TITLE,
		}),
		[],
	);

	if (board == null || board.__typename !== 'JiraBoard' || board.boardId == null) {
		return null;
	}

	return (
		<BaseHeader
			title={board.name}
			isLoading={board == null}
			extraComponentsClose={[
				!showInviteButton && (
					<NavigationTeamButton key="TeamButton" projectFragment={project ?? null} scope="board" />
				),
				showInviteButton && (
					<NavigationInviteButton
						key="InviteButton"
						projectFragment={project ?? null}
						scope="board"
					/>
				),
				<ContextualAnalyticsData key="BoardActions" attributes={analyticsData}>
					<BoardActions
						key="BoardActions"
						board={boardData}
						projectFragment={project ?? null}
						isBoardFavorite={board.favouriteValue?.isFavourite ?? undefined}
						themed={getIsSoftwareThemingM2Enabled()}
					/>
				</ContextualAnalyticsData>,
			]}
			extraComponentsFar={<ExtraComponentsFar projectFragment={project ?? null} />}
			testId="horizontal-nav-header.ui.board-header.header"
			extraComponentsAbove={
				!isFullscreen && (
					<HorizontalNavProjectBreadcrumbs
						projectFragment={project ?? null}
						shouldShowProjectName
					/>
				)
			}
		/>
	);
};
